import { createEffect, createSignal, on, onCleanup, untrack } from 'solid-js';
import { PaneSyncer } from '@trimble/pane-sync';
import { getLogger } from '@trimble/logging';

export type RegisterPaneSyncer = (paneSyncer: PaneSyncer) => () => void;

const MIN_PANES_TO_SYNC = 2;

/**
 * @intent SolidJS primitive that wires up multi-pane camera synchronization.
 * It tracks the panes that register through the returned callback, re-baselines
 * their caches after a view load, and runs the `requestAnimationFrame` loop
 * that fans changes out from the active pane to the others. Call inside a
 * reactive owner (e.g. a component body) so its effects and the rAF loop are
 * disposed with that owner.
 *
 * @returns a `registerPaneSyncer` callback to pass to each pane as
 * `registerSyncedPane`; it returns an unregister function that removes that
 * pane when called (e.g. from the pane's `onCleanup`).
 */
export function createPaneSync(): RegisterPaneSyncer {
  const logger = getLogger();

  const [panes, setPanes] = createSignal<PaneSyncer[]>([]);

  const registerPaneSyncer: RegisterPaneSyncer = (paneSyncer) => {
    setPanes((list) => [...list, paneSyncer]);
    return () => {
      setPanes((list) => list.filter((p) => p !== paneSyncer));
    };
  };

  // `initialized` is shared between both effects below:
  // - the loading-transition effect sets it to `true` after a view restore
  // - the sync-loop effect resets it to `false` whenever panes change so
  //   newly-added panes still receive the initial sync-from-pane-0
  let initialized = false;

  // When view loading ends, re-baseline every pane's camera cache from its
  // restored position. This prevents the sync loop from treating the
  // view-restore camera moves as user-initiated navigation.
  createEffect(
    on(PaneSyncer.isLoadingView, (isLoading, wasLoading) => {
      if (wasLoading && !isLoading) {
        // untrack so this effect does not re-run when panes change
        const currentPanes = untrack(panes);
        if (currentPanes.length >= MIN_PANES_TO_SYNC) {
          for (let i = 0; i < currentPanes.length; i++) {
            currentPanes[i].initialize(i);
          }
          initialized = true;
        }
      }
    })
  );

  createEffect(
    on(panes, (registeredPanes) => {
      // Reset so that panes added outside a view load still get sync-from-pane-0.
      initialized = false;

      if (registeredPanes.length < MIN_PANES_TO_SYNC) {
        return;
      }

      // `stopped` is local to each invocation: onUpdate is async and may be
      // mid-await when panes change. A shared flag would be reset by the
      // new invocation before the old loop resumes.
      let stopped = false;

      const onUpdate = async (): Promise<void> => {
        if (stopped) return;

        // Pause the sync loop while a multi-pane view is loading. Keep the rAF
        // running so we resume automatically once loading finishes, and defer
        // the first-frame initialization until we have a stable camera state.
        if (PaneSyncer.isLoadingView()) {
          requestAnimationFrame((() => {
            void onUpdate();
          }) as FrameRequestCallback);
          return;
        }

        if (!initialized) {
          for (let index = 0; index < registeredPanes.length; index++) {
            registeredPanes[index].initialize(index);
          }
          initialized = true;
          logger.debug(
            `${new Date().toISOString()}: createPaneSync: initialized all panes`
          );

          const sourceRegistry = registeredPanes[0].getRegistry();
          for (let i = 1; i < registeredPanes.length; i++) {
            await registeredPanes[i].triggerSync(sourceRegistry, i);
          }
          registeredPanes[0].syncCameraCache();
        }

        for (let index = 0; index < registeredPanes.length; index++) {
          const pane = registeredPanes[index];

          if (pane.hasChanged(index)) {
            logger.debug(
              `${new Date().toISOString()}: createPaneSync: pane ${index.toString()} has changed, syncing others`
            );
            const sourceRegistry = pane.getRegistry();
            // Cached before the targets sync, because a target sync awaits its
            // navigation: a source change made meanwhile syncs on the next frame.
            pane.syncCameraCache();

            for (
              let otherIndex = 0;
              otherIndex < registeredPanes.length;
              otherIndex++
            ) {
              if (otherIndex === index) continue;

              logger.debug(
                `    ${new Date().toISOString()}: createPaneSync: syncing pane ${otherIndex.toString()}`
              );
              await registeredPanes[otherIndex].triggerSync(
                sourceRegistry,
                otherIndex
              );
            }

            break;
          }
        }

        requestAnimationFrame((() => {
          void onUpdate();
        }) as FrameRequestCallback);
      };

      requestAnimationFrame((() => {
        void onUpdate();
      }) as FrameRequestCallback);

      onCleanup(() => {
        stopped = true;
      });
    })
  );

  return registerPaneSyncer;
}
